// frontend/src/lib/fxRates.js
/**
 * FX Rates (Phase 1 Hybrid) — BACKWARDS COMPAT
 *
 * Purpose:
 * - Provide timestamped, cached FX rates for DISPLAY conversion only
 * - Keep deterministic behavior and tight guardrails
 *
 * Cache policy:
 * - Stored in localStorage
 * - Re-fetch if older than maxAgeHours (default 12)
 * - Optional forceRefresh bypasses cache
 *
 * Source (preferred):
 * - AI Fine Protector backend proxy: http://127.0.0.1:8000/api/fx/latest (dev)
 *
 * Upstream (fallback):
 * - Frankfurter public API:
 *   https://api.frankfurter.dev/v1/latest
 *
 * Notes:
 * - This is for planning display only (not accounting-grade).
 * - UI must disclose: source + as-of timestamp + planning-only.
 */

const FX_CACHE_KEY = "aifine_fx_cache_v1";

function safeParse(raw, fallback) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function nowIso() {
  return new Date().toISOString();
}

function hoursBetween(aIso, bIso) {
  const a = new Date(String(aIso || ""));
  const b = new Date(String(bIso || ""));
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return Infinity;
  return Math.abs(b.getTime() - a.getTime()) / (1000 * 60 * 60);
}

function upper(s) {
  return String(s || "").trim().toUpperCase();
}

export function guessCurrencySymbol(code) {
  const c = upper(code);
  if (c === "USD") return "$";
  if (c === "GBP") return "£";
  if (c === "EUR") return "€";
  return "$";
}

// Prefer explicit backend base if provided
function getApiBase() {
  // If you later set REACT_APP_API_BASE to your deployed API, this will follow it.
  return String(process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000").replace(/\/$/, "");
}

async function fetchJson(url) {
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`FX fetch failed (${res.status}) ${txt || ""}`.trim());
  }
  return res.json();
}

/**
 * Cache shape:
 * {
 *   version: "fx.2",
 *   source_label: "...",
 *   retrieved_at_iso: "...",
 *   as_of_iso: "...",     // based on API date
 *   base: "EUR",
 *   rates: { USD: 1.08, GBP: 0.85, ... }  // 1 base -> X
 * }
 */
export async function getCachedFxRates({ maxAgeHours = 12, forceRefresh = false } = {}) {
  // Try cache first (unless forced)
  if (!forceRefresh) {
    const cached = safeParse(localStorage.getItem(FX_CACHE_KEY), null);
    if (
      cached?.retrieved_at_iso &&
      hoursBetween(cached.retrieved_at_iso, nowIso()) <= Number(maxAgeHours || 12) &&
      cached?.rates &&
      typeof cached.rates === "object" &&
      Object.keys(cached.rates).length > 0
    ) {
      return cached;
    }
  }

  // 1) Preferred: our own backend proxy (avoids browser CORS & gives consistent behavior)
  const proxyUrl = `${getApiBase()}/api/fx/latest`;

  try {
    // Expect proxy to return Frankfurter-like payload: { base, date, rates }
    const data = await fetchJson(proxyUrl);
    const base = upper(data?.base || "EUR");
    const ratesRaw = data?.rates && typeof data.rates === "object" ? data.rates : {};

    const rates = {};
    Object.keys(ratesRaw).forEach((k) => {
      const c = upper(k);
      const v = Number(ratesRaw[k]);
      if (c && Number.isFinite(v) && v > 0) rates[c] = v;
    });

    const asOfIso = data?.date ? new Date(String(data.date)).toISOString() : nowIso();

    const cache = {
      version: "fx.2",
      source_label: "Frankfurter (ECB reference rates) via AI Fine Protector proxy",
      retrieved_at_iso: nowIso(),
      as_of_iso: asOfIso,
      base,
      rates,
    };

    try {
      localStorage.setItem(FX_CACHE_KEY, JSON.stringify(cache));
    } catch {
      // ignore
    }

    return cache;
  } catch (err) {
    // 2) Fallback: direct call (may work in some environments, but keep it as last resort)
    // New official API: https://api.frankfurter.dev/v1/latest
    const fallbackUrl = "https://api.frankfurter.dev/v1/latest";
    const data = await fetchJson(fallbackUrl);

    const base = upper(data?.base || "EUR");
    const ratesRaw = data?.rates && typeof data.rates === "object" ? data.rates : {};

    const rates = {};
    Object.keys(ratesRaw).forEach((k) => {
      const c = upper(k);
      const v = Number(ratesRaw[k]);
      if (c && Number.isFinite(v) && v > 0) rates[c] = v;
    });

    const asOfIso = data?.date ? new Date(String(data.date)).toISOString() : nowIso();

    const cache = {
      version: "fx.2",
      source_label: "Frankfurter (ECB reference rates) direct",
      retrieved_at_iso: nowIso(),
      as_of_iso: asOfIso,
      base,
      rates,
    };

    try {
      localStorage.setItem(FX_CACHE_KEY, JSON.stringify(cache));
    } catch {
      // ignore
    }

    return cache;
  }
}

/**
 * Convert amount from -> to using cache.
 * Returns number or null if missing rate.
 *
 * Frankfurter:
 * - rates[X] = X per 1 base (EUR by default)
 * So:
 *   amount in base = amount / rate[from]  (if from != base)
 *   amount in to   = baseAmount * rate[to] (if to != base)
 */
export function convertMoney(amount, fromCode, toCode, cache) {
  const amt = Number(amount);
  if (!Number.isFinite(amt)) return null;

  const from = upper(fromCode);
  const to = upper(toCode);
  if (!from || !to) return null;
  if (from === to) return amt;

  const base = upper(cache?.base || "EUR");
  const rates = cache?.rates && typeof cache.rates === "object" ? cache.rates : {};

  const rate = (ccy) => {
    const c = upper(ccy);
    if (c === base) return 1;
    const v = Number(rates?.[c]);
    return Number.isFinite(v) && v > 0 ? v : null;
  };

  const rFrom = rate(from);
  const rTo = rate(to);
  if (rFrom == null || rTo == null) return null;

  const inBase = from === base ? amt : amt / rFrom;
  const out = to === base ? inBase : inBase * rTo;

  return Number.isFinite(out) ? out : null;
}

// ---------------------------------------------------------------------------
// Backwards-compatible exports (older imports)
// ---------------------------------------------------------------------------

/**
 * Legacy name used by paidExposureEngine.js (and others).
 *
 * Supports BOTH signatures:
 * 1) Positional (legacy):
 *    convertCurrencyUsingFx(amount, fromCode, toCode, fxCache)
 * 2) Object (newer call sites):
 *    convertCurrencyUsingFx({ amount, from, to, fx })
 *
 * Returns number or null.
 */
export function convertCurrencyUsingFx(a, b, c, d) {
  // Object signature
  if (a && typeof a === "object") {
    const amount = a.amount;
    const from = a.from || a.fromCurrencyCode || a.from_code;
    const to = a.to || a.toCurrencyCode || a.to_code;
    const fxCache = a.fx || a.fxCache || a.cache;
    return convertMoney(amount, from, to, fxCache);
  }

  // Positional signature
  return convertMoney(a, b, c, d);
}

/**
 * Legacy name used by AuditAssessment.jsx
 * Returns a "planning FX object" with the fields the UI expects.
 *
 * Supports:
 * await getFxRatesPlanning({ maxAgeHours: 12, forceRefresh: false })
 */
export async function getFxRatesPlanning({ maxAgeHours = 12, forceRefresh = false } = {}) {
  const cache = await getCachedFxRates({ maxAgeHours, forceRefresh });

  return {
    fx_ok: Boolean(cache && cache.rates && Object.keys(cache.rates).length),
    source_label: cache?.source_label || "Unknown FX source",
    retrieved_at_iso: cache?.retrieved_at_iso || null,
    as_of_iso: cache?.as_of_iso || null,
    base: cache?.base || "EUR",
    rates: cache?.rates || {},
    cache_version: cache?.version || "unknown",
  };
}

export function readFxCache() {
  return safeParse(localStorage.getItem(FX_CACHE_KEY), null);
}

export function clearFxCache() {
  try {
    localStorage.removeItem(FX_CACHE_KEY);
  } catch {
    // ignore
  }
}